// apps/web/src/components/ToastStack/useToastStack.ts
// ─────────────────────────────────────────────────────────────────────────
// Unifies the two independent toast sources — the economy queue (gem + reward
// toasts) and the badges queue (badge toasts) — into ONE ordered, source-
// tagged stack, without merging the underlying Redux slices.
//
// Why a presentation-layer merge instead of one slice: the gem/XP economy and
// the permanent-proof badge systems are deliberately separate data models
// (different idempotency, different persistence, badges grant no currency).
// Collapsing them into a single slice would be a data-model migration that
// belongs with the backend phase. This hook gives the user the single visual
// stack they asked for today, while each source keeps owning its own state.
//
// Ordering: items are merged by a monotonic sequence so a gem toast and a
// badge toast fired from the same action interleave in fire order. Each source
// already pushes in arrival order; we tag arrival with a per-id sequence the
// first time we see an id, so re-renders don't reshuffle the stack.
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	selectToastQueue,
	dismissToast,
	type ToastItem as EconomyToastItem,
} from '../../store/economySlice';
import {
	selectBadgeToastQueue,
	dismissBadgeToast,
	type BadgeToastItem,
} from '../../store/badgesSlice';

/** A unified stack entry: the source slice + the original item. */
export type UnifiedToast =
	| { source: 'economy'; id: string; seq: number; item: EconomyToastItem }
	| { source: 'badges'; id: string; seq: number; item: BadgeToastItem };

/** Newest-last ordering cap. Older overflow items stay queued, just unrendered. */
export const MAX_VISIBLE = 3;

export interface UseToastStackResult {
	/** Oldest → newest, capped to MAX_VISIBLE. */
	visible: UnifiedToast[];
	/** Total queued across both sources (for the +N timing bonus). */
	totalCount: number;
	dismiss: (t: UnifiedToast) => void;
}

export function useToastStack(): UseToastStackResult {
	const economyQueue = useSelector(selectToastQueue);
	const badgeQueue = useSelector(selectBadgeToastQueue);
	const dispatch = useDispatch();

	// Stable per-id arrival sequence. Assigned once, never reshuffled.
	const seqMap = useRef<Map<string, number>>(new Map());
	const seqCounter = useRef(0);

	const assignSeq = useCallback((id: string): number => {
		const existing = seqMap.current.get(id);
		if (existing !== undefined) return existing;
		const next = seqCounter.current++;
		seqMap.current.set(id, next);
		return next;
	}, []);

	const merged = useMemo(() => {
		const all: UnifiedToast[] = [
			...economyQueue.map(
				(item): UnifiedToast => ({
					source: 'economy',
					id: item.id,
					seq: assignSeq(item.id),
					item,
				}),
			),
			...badgeQueue.map(
				(item): UnifiedToast => ({
					source: 'badges',
					id: item.id,
					seq: assignSeq(item.id),
					item,
				}),
			),
		];

		// Prune seqMap entries for ids no longer present (prevent unbounded growth).
		const live = new Set(all.map((t) => t.id));
		for (const id of seqMap.current.keys()) {
			if (!live.has(id)) seqMap.current.delete(id);
		}

		all.sort((a, b) => a.seq - b.seq);
		return all;
	}, [economyQueue, badgeQueue, assignSeq]);

	const visible = useMemo(() => merged.slice(-MAX_VISIBLE), [merged]);

	const dismiss = useCallback(
		(t: UnifiedToast) => {
			if (t.source === 'economy') dispatch(dismissToast(t.id));
			else dispatch(dismissBadgeToast(t.id));
		},
		[dispatch],
	);

	return { visible, totalCount: merged.length, dismiss };
}
